import React, { useState, useRef, useEffect } from 'react';
import { FieldOption } from './translateFields';

interface FieldMultiSelectProps {
    options: FieldOption[];
    selected: string[];
    onChange: (selected: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
}

/**
 * Searchable multi-select for target fields. Handles large field lists (200+)
 * via type-ahead filtering. Selected fields shown as removable tokens.
 */
const FieldMultiSelect: React.FC<FieldMultiSelectProps> = ({
    options,
    selected,
    onChange,
    disabled = false,
    placeholder = 'Search and select fields...',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Reset the filter whenever the menu closes so it doesn't persist on reopen.
    useEffect(() => {
        if (!isOpen) setFilter('');
    }, [isOpen]);

    const labelFor = (value: string): string => {
        const opt = options.find(o => o.value === value);
        return opt ? opt.label : value;
    };

    const filtered = filter
        ? options.filter(o =>
            o.label.toLowerCase().includes(filter.toLowerCase())
            || o.value.toLowerCase().includes(filter.toLowerCase()))
        : options;

    const toggleField = (value: string) => {
        if (disabled) return;
        onChange(
            selected.includes(value)
                ? selected.filter(v => v !== value)
                : [...selected, value]
        );
    };

    return (
        <div className="field-multiselect" ref={containerRef}>
            <div
                className={`field-multiselect-trigger ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <span className="field-multiselect-summary">
                    {selected.length === 0
                        ? placeholder
                        : `${selected.length} field${selected.length === 1 ? '' : 's'} selected`}
                </span>
                <span className="field-multiselect-arrow">{isOpen ? '▲' : '▼'}</span>
            </div>

            {isOpen && !disabled && (
                <div className="field-multiselect-menu">
                    <input
                        type="text"
                        className="field-multiselect-search"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        placeholder="Filter fields..."
                        autoFocus
                    />
                    <div className="field-multiselect-options">
                        {filtered.length === 0 ? (
                            <div className="field-multiselect-empty">No matching fields</div>
                        ) : (
                            filtered.map(opt => (
                                <div
                                    key={opt.value}
                                    className={`field-multiselect-option ${selected.includes(opt.value) ? 'selected' : ''}`}
                                    onClick={() => toggleField(opt.value)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(opt.value)}
                                        onChange={() => {}}
                                    />
                                    <span className="field-multiselect-option-label">{opt.label}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {selected.length > 0 && (
                <div className="field-multiselect-tokens">
                    {selected.map(value => (
                        <span key={value} className="field-token">
                            {labelFor(value)}
                            <button
                                type="button"
                                className="field-token-remove"
                                onClick={() => toggleField(value)}
                                disabled={disabled}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FieldMultiSelect;
